import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Eye, ExternalLink, Copy, Search, X } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale/pt-BR';
import { passwordsService, Password } from '@/features/passwords/services/passwords.service';
import { passwordCategoriesService } from '@/features/passwords/services/passwords.service';
import { PasswordViewDialog } from '@/features/passwords/components/PasswordViewDialog';
import { useToast } from '@/hooks/use-toast';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useNotionBoards } from '@/features/passwords/hooks/useNotionBoards';
import { useTeams } from '@/features/passwords/hooks/useTeams';

export const Route = createFileRoute('/_authenticated/my-passwords')({
  component: MyPasswordsPage,
});

function MyPasswordsPage() {
  const { toast } = useToast();
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedPassword, setSelectedPassword] = useState<Password | null>(null);
  const [search, setSearch] = useState('');
  const [selectedTeamId, setSelectedTeamId] = useState<string>('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [selectedNotionBoardId, setSelectedNotionBoardId] = useState<string>('');

  // Buscar notion boards e teams para filtros
  const { data: notionBoards } = useNotionBoards();
  const { data: teams } = useTeams();
  const { data: categories } = useQuery({
    queryKey: ['password-categories'],
    queryFn: async () => {
      const response = await passwordCategoriesService.getAll();
      return Array.isArray(response.data) ? response.data : [];
    },
  });

  // Buscar senhas da equipe do usuário com filtros
  const {
    data: passwordsData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['my-passwords', search, selectedTeamId, selectedCategoryId, selectedNotionBoardId],
    queryFn: async () => {
      const response = await passwordsService.getMyPasswords({
        search: search || undefined,
        teamId: selectedTeamId || undefined,
        categoryId: selectedCategoryId || undefined,
        notionBoardId: selectedNotionBoardId || undefined,
      });
      return Array.isArray(response.data) ? response.data : [];
    },
  });

  const passwords: Password[] = passwordsData || [];

  const handleView = (password: Password) => {
    setSelectedPassword(password);
    setViewDialogOpen(true);
  };

  const handleAccess = () => {
    // Invalidar query para atualizar lista se necessário
    refetch();
  };

  const handleCopyPassword = async (password: Password) => {
    try {
      const response = await passwordsService.getById(password.id, true);
      const passwordText = response.data.password;
      if (passwordText) {
        navigator.clipboard.writeText(passwordText);
        toast({
          title: 'Copiado!',
          description: 'Senha copiada para a área de transferência',
        });
        await passwordsService.logAccess(password.id, 'copy');
      }
    } catch (error: any) {
      console.error('Erro ao copiar senha:', error);
      const errorMessage =
        error?.response?.data?.message || error?.message || 'Não foi possível copiar a senha';
      toast({
        title: 'Erro',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  const handleCopyLogin = async (password: Password) => {
    if (password.login) {
      navigator.clipboard.writeText(password.login);
      toast({
        title: 'Copiado!',
        description: 'Login copiado para a área de transferência',
      });
      await passwordsService.logAccess(password.id, 'copy_login');
    } else {
      toast({
        title: 'Aviso',
        description: 'Esta senha não possui login cadastrado',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Minhas Senhas</h1>
        <p className="text-muted-foreground">Senhas das equipes em que você está participando</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Senhas Disponíveis</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 mb-6">
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Pesquisar por plataforma ou login..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9"
                  />
                  {search && (
                    <button
                      type="button"
                      onClick={() => setSearch('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
              <div className="relative">
                <Select value={selectedTeamId || undefined} onValueChange={setSelectedTeamId}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Filtrar por equipe" />
                  </SelectTrigger>
                  <SelectContent>
                    {teams?.map((team) => (
                      <SelectItem key={team.id} value={team.id}>
                        {team.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedTeamId && (
                  <button
                    type="button"
                    onClick={() => setSelectedTeamId('')}
                    className="absolute -right-2 -top-2 h-5 w-5 rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90 flex items-center justify-center text-xs"
                    title="Limpar filtro de equipe"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
              <div className="relative">
                <Select
                  value={selectedCategoryId || undefined}
                  onValueChange={setSelectedCategoryId}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Filtrar por categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories?.map((category) => (
                      <SelectItem key={category.id} value={category.id}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedCategoryId && (
                  <button
                    type="button"
                    onClick={() => setSelectedCategoryId('')}
                    className="absolute -right-2 -top-2 h-5 w-5 rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90 flex items-center justify-center text-xs"
                    title="Limpar filtro de categoria"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
              <div className="relative">
                <Select value={selectedNotionBoardId || undefined} onValueChange={setSelectedNotionBoardId}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Filtrar por cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    {notionBoards?.map((board) => (
                      <SelectItem key={board.id} value={board.id}>
                        {board.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedNotionBoardId && (
                  <button
                    type="button"
                    onClick={() => setSelectedNotionBoardId('')}
                    className="absolute -right-2 -top-2 h-5 w-5 rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90 flex items-center justify-center text-xs"
                    title="Limpar filtro de cliente"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
              {(search || selectedTeamId || selectedCategoryId || selectedNotionBoardId) && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setSearch('');
                    setSelectedTeamId('');
                    setSelectedCategoryId('');
                    setSelectedNotionBoardId('');
                  }}
                >
                  <X className="mr-2 h-4 w-4" />
                  Limpar Filtros
                </Button>
              )}
            </div>
          </div>
          {isLoading ? (
            <p className="text-center text-muted-foreground py-8">Carregando...</p>
          ) : passwords.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              Nenhuma senha encontrada
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Plataforma</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Equipes</TableHead>
                  <TableHead>Categorias</TableHead>
                  <TableHead>Link</TableHead>
                  <TableHead>Criada em</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {passwords.map((password) => (
                  <TableRow key={password.id}>
                    <TableCell className="font-medium">{password.platform}</TableCell>
                    <TableCell>
                      {password.notionBoard ? (
                        <span className="inline-flex items-center rounded-md bg-primary/10 px-2 py-1 text-xs font-medium text-primary">
                          {password.notionBoard.name}
                        </span>
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {password.teams && password.teams.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {password.teams.map((team) => (
                            <span
                              key={team.id}
                              className="inline-flex items-center rounded-md bg-secondary px-2 py-1 text-xs font-medium"
                            >
                              {team.name}
                            </span>
                          ))}
                        </div>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {password.categories && password.categories.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {password.categories.map((category) => (
                            <span
                              key={category.id}
                              className="inline-flex items-center rounded-md bg-primary/10 px-2 py-1 text-xs font-medium text-primary"
                            >
                              {category.name}
                            </span>
                          ))}
                        </div>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {password.link ? (
                        <a
                          href={password.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline flex items-center gap-1"
                        >
                          Abrir
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {format(new Date(password.createdAt), 'dd/MM/yyyy', { locale: ptBR })}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCopyPassword(password)}
                          title="Copiar Senha"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        {password.login && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCopyLogin(password)}
                            title="Copiar Login"
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleView(password)}
                          title="Ver Senha"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <PasswordViewDialog
        open={viewDialogOpen}
        onOpenChange={setViewDialogOpen}
        password={selectedPassword}
        onAccess={handleAccess}
      />
    </div>
  );
}
